/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';
import { ButtonLike } from '@components/button-like';
import { ButtonSave } from '@components/button-save';
import { Creators } from '@components/creators';
import { Box, Container, Text, Button } from '@components/design-system';
import { ItemTabs } from '@components/item-tabs';
import { Purchase } from '@components/purchase';
import { DATE_OPTIONS } from '@constants';
import { IToken } from '@interface/token';
import { actions } from '@state/token';
import { getURLFromIPFSURI } from '@utils/ipfs';
import { extractMedium } from '@utils/token';
import * as Styles from './styles';
import { Titles } from './titles';
import { CuratedOn } from '@components/curated-on';

const MediaWrapper = dynamic(() => import('@components/media'), { ssr: false });

const ItemSignCollaboration = dynamic<any>(
  import('@components/item-sign-collaboration').then((e) => e.ItemSignCollaboration),
  { ssr: false }
);

export interface IContainerToken {
  token: IToken;
}

export const ContainerToken: FC<IContainerToken> = ({ token }) => {
  const t = useTranslations('token');
  const dispatch = useDispatch();
  const { locale } = useRouter();

  const { ref, inView } = useInView({
    threshold: 0.001,
  });

  useEffect(() => {
    dispatch({ type: actions.TOKEN_UPDATE_TOKEN, payload: { id: token.id, creators: token.creators } });
  }, [token.id]);

  const getLicenseFromKey = (key: string) => {
    switch (key) {
      case 'custom':
        return 'Custom License';
      case 'public':
        return 'Public Domain';
      default:
        return key;
    }
  };

  const flashing = token.accessibility?.hazards ? token.accessibility?.hazards.indexOf('flashing') !== -1 : false;

  return (
    <Styles.Container>
      <Styles.Media>
        <MediaWrapper key={token.id} token={token} labels={false} inDetailView />
      </Styles.Media>

      <Container css={{ height: 64 }}>
        {flashing && (
          <Box css={{ height: '100%', i: { fontSize: 16 }, '@bp1': { display: 'none' } }} vertical>
            <i>bolt</i>
            <Text unset css={{ fontSize: 14, color: '$secondaryFill' }}>
              Photosensitive seizure warning
            </Text>
          </Box>
        )}
        <Box css={{ display: 'none', '@bp1': { display: 'flex' } }}>
          <Titles inView={inView} title={token.title} creators={token.creators} accessibility={token.accessibility} />
        </Box>
      </Container>

      <div ref={ref}>
        <Container
          css={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '$space$1',

            '@bp1': {
              flexDirection: 'unset',
            },
          }}
        >
          <Box
            css={{
              flexDirection: 'column',
              lineBreak: 'auto',
              overflow: 'hidden',
              width: '100%',

              '@bp1': {
                width: 'calc(100% - $space$2 -  350px)',
                marginRight: '$space$2',
              },
            }}
          >
            <ItemSignCollaboration tokenId={token.token_id} />

            <Box css={{ padding: '$space$5 0' }} vertical>
              <Creators creators={token.creators} avatar />
            </Box>

            <Text as="h3" unset>
              {token.title}
            </Text>

            <Box vertical css={{ marginBottom: '$space$2', padding: '$space$4 0', '&>*': { marginRight: '$space$4' } }}>
              <ButtonLike key={token.id} tokenId={token.id} />
              <ButtonSave tokenId={token.id} />
            </Box>

            <Box css={{ flexDirection: 'column', marginBottom: '$space$2' }}>
              <Text
                unset
                css={{
                  color: '$secondaryFill',
                  maxWidth: 1024,
                  whiteSpace: 'pre-wrap',
                  lineBreak: 'auto',
                  overflow: 'hidden',
                }}
              >
                {token.description}
              </Text>
            </Box>

            <Box css={{ marginBottom: '$space$2' }}>
              <Box css={{ flexFlow: 'row wrap', display: 'flex', '& > *': { marginRight: 8, marginBottom: 8 } }}>
                {token.tags.map(({ tag }: any) => (
                  <Link key={tag.id} href={`/explore?tags=${tag.id}`} locale={locale} prefetch={false} passHref>
                    <Button variant="tag" as="a">
                      {tag.name}
                    </Button>
                  </Link>
                ))}
              </Box>
            </Box>

            <Box css={{ flexDirection: 'column', marginBottom: '$space$2' }}>
              <Text css={{ fontWeight: 700, marginBottom: '$space$5' }}>
                {t('editions', { edition: token.supply })}
              </Text>
              <Text unset css={{ color: '$secondaryFill', fontStyle: 'italic', fontSize: 14 }}>
                Medium: {extractMedium(token)}. Minted on:{' '}
                {new Date(token.timestamp).toLocaleDateString(locale, DATE_OPTIONS)}.{' '}
                {token.rights !== 'none' && token.right_uri !== '' && (
                  <>
                    Artwork released under&nbsp;
                    <Button
                      as="a"
                      variant="underline"
                      href={getURLFromIPFSURI(token.right_uri)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getLicenseFromKey(token.rights)}
                    </Button>
                    .
                  </>
                )}
              </Text>
            </Box>
          </Box>

          <Box
            css={{
              width: '100%',
              margin: '$space$2 0',

              '@bp1': {
                width: 350,
                margin: 'unset',
              },
            }}
          >
            <Purchase token={token} />
          </Box>
        </Container>

        <Container>
          <ItemTabs token={token} />
        </Container>

        <Container css={{ marginBottom: '$space$1' }}>
          <CuratedOn id={token.id} />
        </Container>
      </div>
    </Styles.Container>
  );
};
